import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useCart } from '@/lib/cart';
import { Search, ShoppingCart, Heart, User, Menu, X, ChevronDown } from 'lucide-react';

const navLinks = [
  { name: 'Home', href: '/' },
  { name: 'All Categories', href: '/shop' },
  { name: 'Mobile', href: '/category/smartphones' },
  { name: 'Audio', href: '/category/audio' },
  { name: 'Computer', href: '/category/laptops' },
  { name: 'Smart Home', href: '/category/smart-home' },
  { name: 'Wearables', href: '/category/wearables' },
];

interface HeaderProps {
  onSearchOpen: () => void;
  onCartOpen: () => void;
  darkMode: boolean;
  onToggleDarkMode: () => void;
}

export function Header({ onSearchOpen, onCartOpen, darkMode, onToggleDarkMode }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(true);
  const { getItemCount } = useCart();
  const itemCount = getItemCount();
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB] shadow-sm" role="banner">
      <AnimatePresence>
        {bannerVisible && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-gradient-to-r from-[#FE2C55] to-[#FF5277] text-white text-center text-[13px] font-medium relative overflow-hidden"
          >
            <div className="py-2 px-4">
              FREE SHIPPING on orders over S$50 | Use code <span className="font-bold">SGGADGETS</span> for 10% off!
            </div>
            <button
              onClick={() => setBannerVisible(false)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-lg"
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-[1280px] mx-auto px-5">
        <div className="flex items-center gap-6 py-3">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 bg-[#FE2C55] rounded-lg flex items-center justify-center text-white font-black text-lg">S</div>
            <span className="text-[22px] font-extrabold text-[#FE2C55]">SG<span className="text-[#1A1A2E]">Gadgets</span></span>
          </Link>

          <div className="flex-1 max-w-[680px] hidden md:flex border-2 border-[#FE2C55] rounded-full overflow-hidden focus-within:shadow-[0_0_0_3px_rgba(254,44,85,0.15)] transition-all">
            <input
              type="text"
              placeholder="Search for gadgets, electronics, accessories..."
              className="flex-1 border-none px-5 py-2.5 text-sm outline-none min-w-0 bg-transparent"
              onClick={onSearchOpen}
              readOnly
            />
            <button
              onClick={onSearchOpen}
              className="bg-[#FE2C55] text-white px-6 font-semibold text-sm hover:bg-[#E8254A] transition-colors"
            >
              Search
            </button>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
            <Link to="/wishlist" className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded text-[#6B7280] text-[11px] hover:text-[#FE2C55] hover:bg-[#FFF5F7] transition-all">
              <Heart className="w-5 h-5" />
              <span className="hidden lg:inline">Wishlist</span>
            </Link>

            <button
              onClick={onCartOpen}
              className="relative flex flex-col items-center gap-0.5 px-3 py-1.5 rounded text-[#6B7280] text-[11px] hover:text-[#FE2C55] hover:bg-[#FFF5F7] transition-all"
            >
              <ShoppingCart className="w-5 h-5" />
              <AnimatePresence>
                {itemCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="absolute -top-0.5 right-1 bg-[#FE2C55] text-white text-[10px] font-bold w-[18px] h-[18px] rounded-full flex items-center justify-center"
                  >
                    {itemCount > 99 ? '99+' : itemCount}
                  </motion.span>
                )}
              </AnimatePresence>
              <span className="hidden lg:inline">Cart</span>
            </button>

            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded text-[#6B7280] text-[11px] hover:text-[#FE2C55] hover:bg-[#FFF5F7] transition-all"
              >
                <User className="w-5 h-5" />
                <span className="hidden lg:inline">Account</span>
              </button>

              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-[#E5E7EB] py-2 z-50"
                  >
                    <Link to="/login" className="block px-4 py-2 text-sm hover:bg-[#F3F4F6]" onClick={() => setUserMenuOpen(false)}>Sign In</Link>
                    <Link to="/register" className="block px-4 py-2 text-sm hover:bg-[#F3F4F6]" onClick={() => setUserMenuOpen(false)}>Create Account</Link>
                    <div className="my-1 border-t border-[#E5E7EB]" />
                    <Link to="/account" className="block px-4 py-2 text-sm hover:bg-[#F3F4F6]" onClick={() => setUserMenuOpen(false)}>My Account</Link>
                    <Link to="/account/orders" className="block px-4 py-2 text-sm hover:bg-[#F3F4F6]" onClick={() => setUserMenuOpen(false)}>Orders</Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      <nav className="border-t border-[#E5E7EB] bg-white">
        <div className="max-w-[1280px] mx-auto px-5 flex items-center gap-0 overflow-x-auto">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              className="px-4 py-2.5 text-[13px] font-medium text-[#6B7280] whitespace-nowrap border-b-2 border-transparent hover:text-[#FE2C55] hover:border-[#FE2C55] transition-all"
            >
              {link.name}
            </Link>
          ))}
        </div>
      </nav>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-[#E5E7EB] bg-white overflow-hidden"
          >
            <div className="p-4 space-y-2">
              <div className="flex border-2 border-[#FE2C55] rounded-full overflow-hidden mb-4">
                <input type="text" placeholder="Search..." className="flex-1 border-none px-4 py-2 text-sm outline-none" onClick={onSearchOpen} readOnly />
                <button onClick={onSearchOpen} className="bg-[#FE2C55] text-white px-4 text-sm font-semibold">Search</button>
              </div>
              {navLinks.map((link) => (
                <Link key={link.name} to={link.href} className="block px-3 py-2 text-sm font-medium rounded-lg hover:bg-[#F3F4F6]" onClick={() => setMobileMenuOpen(false)}>
                  {link.name}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="md:hidden fixed bottom-4 right-4 z-50 p-3 bg-[#FE2C55] text-white rounded-full shadow-lg"
      >
        {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>
    </header>
  );
}
